'use strict'

const flow_ = require('lodash/flow')
const isPlainObject_ = require('lodash/isPlainObject')
const includes_ = require('lodash/fp/includes')
const curry_ = require('lodash/curry')
const maybe = require('folktale/maybe')

const queryResults = require('../helpers/queryResults')

const {
  cleanseRecord,
  createDraft,
  getRecordId,
  markRecordDirty
} = require('./records')
const { freshScope } = require('./scopes')

// insertToObjectMap :: (Object, Id) -> Object
const insertToObjectMap = (object, idFn, items) =>
  Object.assign({}, object, ...items.map(item => ({ [idFn(item)]: item })))

// updateInObjectMap :: (Object, Id, Record -> Record) -> Object
const updateInObjectMap = (object, id, updateFn) =>
  Object.assign({}, object, { [id]: updateFn(object[id]) })

// removeFromObjectMap :: (Object, Id) -> Object
const removeFromObjectMap = (object, id) =>
  Object.assign(
    {},
    ...Object.keys(object)
      .filter(mapId => mapId !== id)
      .map(mapId => ({ [mapId]: object[mapId] }))
  )

// freshStore :: () -> Store
const freshStore = () =>
  Object.assign(
    {},
    {
      records: {},
      drafts: {},
      scopes: {}
    }
  )

// setStoreRecords :: RecordsObject -> Store -> Store
const setStoreRecords = records => store =>
  Object.assign({}, store, { records })

// setDrafts :: DraftsObject -> Store -> Store
const setDrafts = drafts => store => Object.assign({}, store, { drafts })

// setScope :: (ScopeKey, Scope) -> Store -> Store
const setScope = (key, scope) => store =>
  Object.assign({}, store, {
    scopes: Object.assign({}, store.scopes, { [key]: scope })
  })

// getScope :: (Store, ScopeKey) -> Scope
const getScope = (store, scopeKey) =>
  store.scopes[scopeKey] !== undefined ? store.scopes[scopeKey] : freshScope()

// iterateScopes :: ((Scope, ScopeKey) -> *, (Scope, ScopeKey) -> Bool) -> Store -> [*]
const iterateScopes = (iterateFn, predicate) => store =>
  Object.keys(store.scopes)
    .filter(key => predicate(store.scopes[key], key))
    .map(key => iterateFn(store.scopes[key], key))

// insertRecords :: [Record] -> Store -> Store
const insertRecords = records => store =>
  setStoreRecords(insertToObjectMap(store.records, getRecordId, records))(store)

// insertRecord :: Record -> Store -> Store
const insertRecord = record => insertRecords([record])

// insertDrafts :: [Draft] -> Store -> Store
const insertDrafts = drafts => store =>
  setDrafts(insertToObjectMap(store.drafts, getRecordId, drafts))(store)

// insertDraft :: Draft -> Store -> Store
const insertDraft = draft => insertDrafts([draft])

// removeRecord :: RecordId -> Store -> Store
const removeRecord = recordId => store =>
  setStoreRecords(removeFromObjectMap(store.records, recordId))(store)

// removeDraft :: Draft -> Store -> Store
const removeDraft = draft => store =>
  setDrafts(removeFromObjectMap(store.drafts, getRecordId(draft)))(store)

// resetDraft :: (Draft, DefaultDraft) -> Store -> Store
const resetDraft = (draft, defaultDraft) => store =>
  setDrafts(
    updateInObjectMap(store.drafts, getRecordId(draft), d =>
      createDraft(d, defaultDraft)
    )
  )(store)

// updateScope :: (ScopeKey, Scope -> Scope) -> Store -> Store
const updateScope = (scopeKey, updateFn) => store =>
  setScope(scopeKey, updateFn(getScope(store, scopeKey)))(store)

// storeQueryResults :: QueryResults -> Store -> Store
const storeQueryResults = queryResult =>
  queryResult.matchWith({
    Empty: () => _ => _,
    Results: ({ items, totalCount, offset }) =>
      items.length > 0 ? insertRecords(items) : _ => _
  })

// updateRecordFields :: (RecordId, FieldsValues) -> Store -> Store
const updateRecordFields = (recordId, fieldValues) => store =>
  flow_(
    setDrafts(
      updateInObjectMap(store.drafts, recordId, draft =>
        Object.assign({}, markRecordDirty(draft), fieldValues)
      )
    )
  )(store)

// doesRecordExist :: (Store, Id) -> Bool
const doesRecordExist = (store, id) => !!store.records[id]

// getDraftOrRecord :: (Store, RecordOrDraftId) -> Maybe MergedRecord
const getDraftOrRecord = (store, id) => {
  if (isPlainObject_(store.records[id])) {
    const mergedRecord = Object.assign({}, store.records[id], store.drafts[id])

    return maybe.Just(mergedRecord)
  } else {
    return isPlainObject_(store.drafts[id])
      ? maybe.Just(Object.assign({}, store.drafts[id]))
      : maybe.Nothing()
  }
}

// readFromRecordsStore :: (ScopeKey, Integer, Integer) -> Store -> Boolean -> QueryResults
const readFromRecordsStore = curry_(
  (scopeKey, fromIndex, toIndex, store, allowMissing = false) => {
    const scope = getScope(store, scopeKey)
    const numRequestedRecords = toIndex - fromIndex
    const matchingRecords = scope.records.slice(fromIndex, toIndex)

    const hasRequestedMatchingItems = ({ items }) =>
      allowMissing ||
      (numRequestedRecords > 0 && items.length >= numRequestedRecords)
    const isNotSparse = ({ items }) =>
      !(includes_(null, items) || includes_(undefined, items))

    return queryResults
      .of({
        items: matchingRecords,
        totalCount: scope.numMatchingRecords,
        offset: fromIndex
      })
      .filter(hasRequestedMatchingItems)
      .map(({ items, totalCount, offset }) => ({
        items: items.map(id =>
          cleanseRecord(getDraftOrRecord(store, id).getOrElse({}))
        ),
        totalCount,
        offset
      }))
      .filter(isNotSparse)
  }
)

module.exports.doesRecordExist = doesRecordExist
module.exports.freshStore = freshStore
module.exports.getDraftOrRecord = getDraftOrRecord
module.exports.getScope = getScope
module.exports.insertDraft = insertDraft
module.exports.insertRecord = insertRecord
module.exports.iterateScopes = iterateScopes
module.exports.readFromRecordsStore = readFromRecordsStore
module.exports.removeDraft = removeDraft
module.exports.removeRecord = removeRecord
module.exports.resetDraft = resetDraft
module.exports.setScope = setScope
module.exports.storeQueryResults = storeQueryResults
module.exports.updateRecordFields = updateRecordFields
module.exports.updateScope = updateScope



// WEBPACK FOOTER //
// ./recordStore/store.js